import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { notificationLanguagesQuery } from 'api/queries';
import { ParseKeys } from 'i18next';
import {
	ControlledSelect,
	ControlledSelectProps,
} from 'library/components/controls/form/ControlledSelect';

export type NotificationLanguageSelectProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControlledSelectProps<TFieldValues, TName>, 'options'>;

export const NotificationLanguageSelect = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: NotificationLanguageSelectProps<TFieldValues, TName>
): React.JSX.Element => {
	const { api } = useProductCenterApi();
	const { data } = useQuery(notificationLanguagesQuery(api));
	const { t } = useTranslation('customer');

	const notificationLanguagesData = useMemo(
		() =>
			data?.map(({ languageCode }) => {
				const [lang, region] = languageCode.split('-');
				const languageCodePc = `${lang.toLowerCase()}-${region.toUpperCase()}`;
				const languageKey = `${lang.toLowerCase()}_${region.toUpperCase()}`;

				return {
					id: languageCodePc,
					label: t(
						`customer:NotificationLanguage_${languageKey}` as ParseKeys<'customer'>
					),
				};
			}),
		[data, t]
	)?.sort((a, b) => a.label.localeCompare(b.label));

	return (
		<ControlledSelect
			options={notificationLanguagesData || []}
			{...props}
		/>
	);
};
